import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { api } from "@/api";

const typeLabel = (type) => ({ listing: "Listing", member: "Member", feed: "Feed", "admin-user": "Admin user", "admin-order": "Admin order" }[type] || type);

export const GlobalSearch = ({ compact = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (location.pathname === "/search") setQuery(params.get("q") || "");
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const timer = setTimeout(() => {
      api.get("/search", { params: { q: query.trim() } })
        .then(({ data }) => setResults(data))
        .catch(() => setResults([]));
    }, 220);
    return () => clearTimeout(timer);
  }, [query]);

  const submit = (event) => {
    event.preventDefault();
    if (query.trim().length < 2) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  const clear = () => {
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  return (
    <form data-testid={compact ? "mobile-global-search-form" : "global-search-form"} onSubmit={submit} className="relative w-full">
      <MagnifyingGlass data-testid={compact ? "mobile-global-search-icon" : "global-search-icon"} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#AEB8AE]" size={17} />
      <input
        data-testid={compact ? "mobile-global-search-input" : "global-search-input"}
        className="h-11 w-full border border-[#2C3D35] bg-[#1A241F] pl-10 pr-16 text-sm text-[#F7F2EA] outline-none transition-colors placeholder:text-[#9BAA9F] focus:border-[#D86F55]"
        placeholder="Search listings, members, feed..."
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
      />
      {query && <button data-testid={compact ? "mobile-global-search-clear" : "global-search-clear"} type="button" className="absolute right-10 top-1/2 -translate-y-1/2 text-[#C8D0C8] hover:text-white" onClick={clear}><X size={15} /></button>}
      <button data-testid={compact ? "mobile-global-search-submit" : "global-search-submit"} type="submit" className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center bg-[#D86F55] text-white"><MagnifyingGlass size={15} /></button>
      {open && query.trim().length >= 2 && <div data-testid={compact ? "mobile-global-search-results" : "global-search-results"} className="absolute left-0 right-0 top-12 z-[70] max-h-96 overflow-auto border border-[#2C3D35] bg-[#111815] shadow-2xl shadow-black/40">
        {results.length === 0 ? <p data-testid={compact ? "mobile-global-search-empty" : "global-search-empty"} className="p-4 text-sm text-[#AEB8AE]">No results yet.</p> : results.slice(0, 8).map((item) => <Link data-testid={`global-search-result-${item.type}-${item.id}`} key={`${item.type}-${item.id}`} to={item.href || "/search"} className="block border-b border-[#2C3D35] p-3 text-left transition-colors hover:bg-[#1A241F]" onClick={() => setOpen(false)}><span data-testid={`global-search-result-type-${item.type}-${item.id}`} className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#D86F55]">{typeLabel(item.type)} · {item.status}</span><span data-testid={`global-search-result-title-${item.type}-${item.id}`} className="mt-1 block truncate text-sm font-semibold text-[#F7F2EA]">{item.title}</span><span data-testid={`global-search-result-subtitle-${item.type}-${item.id}`} className="mt-1 block truncate text-xs text-[#AEB8AE]">{item.subtitle}</span></Link>)}
        <button data-testid={compact ? "mobile-global-search-view-all" : "global-search-view-all"} type="submit" className="w-full bg-[#1A3626] p-3 text-left text-xs font-bold uppercase tracking-[0.18em] text-[#F7F2EA] hover:bg-[#254C35]">View all search results</button>
      </div>}
    </form>
  );
};